import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowBack as ArrowBackIcon,
  Audiotrack,
  Close as CloseIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { formatBytes, getExtension } from "../previewTypes";

interface AudioRendererProps {
  blobUrl: string | null;
  fileName: string;
  fileSize?: number;
  loading?: boolean;
  error?: string | null;
  onDownload?: () => void;
  onClose?: () => void;
  onRetry?: () => void;
  onBack?: () => void;
  backLabel?: string;
  disableDownload?: boolean;
}

const whiteIconSx = {
  color: "common.white",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
  "&.Mui-disabled": { color: alpha("#ffffff", 0.4) },
};

const AudioRenderer: React.FC<AudioRendererProps> = ({
  blobUrl,
  fileName,
  fileSize,
  loading,
  error,
  onDownload,
  onClose,
  onRetry,
  onBack,
  backLabel,
  disableDownload,
}) => {
  const ext = useMemo(() => getExtension(fileName).toUpperCase(), [fileName]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        sx={{
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
          color: "common.white",
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 1.25, sm: 1.5 },
          pb: { xs: 2, sm: 2.5 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {onBack && (
            <Tooltip title={backLabel || "Back"}>
              <IconButton
                size="small"
                onClick={onBack}
                sx={whiteIconSx}
                aria-label={backLabel || "Back"}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          {onDownload && (
            <Tooltip title="Download">
              <span>
                <IconButton
                  size="small"
                  onClick={onDownload}
                  disabled={disableDownload}
                  sx={whiteIconSx}
                  aria-label={`Download ${fileName}`}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onClose && (
            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={onClose}
                sx={whiteIconSx}
                aria-label="Close preview"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={2.25}
          sx={{ minWidth: 0, mt: 0.5 }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.22)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
            }}
          >
            <Audiotrack sx={{ fontSize: 32, color: "common.white" }} />
          </Box>
          <Stack sx={{ minWidth: 0, flex: 1 }} spacing={0.25}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={fileName}
            >
              {fileName}
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.85, letterSpacing: 0.5 }}
            >
              {ext ? `${ext} audio` : "Audio"}
              {fileSize ? ` • ${formatBytes(fileSize)}` : ""}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          backgroundColor: "background.paper",
          px: { xs: 2, sm: 2.5 },
          py: { xs: 2, sm: 2.25 },
        }}
      >
        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.25}
            sx={{ py: 2 }}
          >
            <CircularProgress size={26} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Stack>
        ) : error ? (
          <Stack spacing={1.5}>
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {error}
            </Alert>
            {onRetry && (
              <Button
                size="small"
                variant="outlined"
                onClick={onRetry}
                sx={{ alignSelf: "flex-start" }}
              >
                Retry
              </Button>
            )}
          </Stack>
        ) : blobUrl ? (
          <audio
            src={blobUrl}
            controls
            style={{ width: "100%", display: "block", height: 40 }}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default AudioRenderer;
